import React, { useState, useEffect } from 'react';
import { X, Gamepad2, Trophy } from 'lucide-react';
import axios from 'axios';

const GAMES = [
  { id: 'connect4', name: 'Connect 4', icon: '', description: 'Classic 4-in-a-row' },
  { id: 'checkers', name: 'Checkers', icon: '', description: 'Jump and capture' },
  { id: 'chess', name: 'Chess', icon: '', description: 'Strategic battle' },
  { id: 'battleship', name: 'Battleship', icon: '', description: 'Sink the fleet' }
];

const GameInviteModal = ({ isOpen, onClose, friend, onInvite }) => {
  const [selectedGame, setSelectedGame] = useState(null);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && friend) {
      fetchStats();
    }
  }, [isOpen, friend]);
  
  const fetchStats = async () => {
    try {
      setLoading(true);
      const apiBase = process.env.REACT_APP_API_BASE || '';
      const res = await axios.get(`${apiBase}/api/games/stats/${friend.user_id}`, {
        withCredentials: true
      });
      setStats(res.data.stats || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getStatsForGame = (gameType) => {
    return stats.find(s => s.gameType === gameType);
  };
  
  const handleInvite = () => {
    if (selectedGame) {
      onInvite(selectedGame);
      setSelectedGame(null);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-md bg-slate-900/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-400/10 border border-emerald-200/20 flex items-center justify-center">
              <Gamepad2 className="text-emerald-400" size={20} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Challenge {friend?.username}</h3>
              <p className="text-sm text-white/50">Choose a game to play</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
          >
            <X className="text-white/70" size={18} />
          </button>
        </div>
        
        {/* Game Selection */}
        <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
          {GAMES.map((game) => {
            const gameStats = getStatsForGame(game.id);
            const isSelected = selectedGame === game.id;
            
            return (
              <button
                key={game.id}
                onClick={() => setSelectedGame(game.id)}
                className={`
                  w-full text-left p-4 rounded-2xl border transition
                  ${isSelected 
                    ? 'bg-emerald-400/15 border-emerald-400/30' 
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-3xl">{game.icon}</div>
                    <div>
                      <div className="font-semibold text-white">{game.name}</div>
                      <div className="text-sm text-white/50">{game.description}</div>
                      {gameStats && (
                        <div className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                          <Trophy size={12} />
                          {gameStats.userWins}W - {gameStats.opponentWins}L - {gameStats.draws}D
                        </div>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="w-5 h-5 rounded-full bg-emerald-400 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-white/10 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={handleInvite}
            disabled={!selectedGame}
            className="flex-1 px-4 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-white/30 text-white font-medium transition"
          >
            Send Invite
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameInviteModal;
